import type { GameObject, ItemRecipe, Shield, Weapon } from '../types';
import { SkillType } from '../model/skills';
import { dmg } from '../model/game';
import { forgeRecipe } from '../model/recipe';
import * as attack from '../data/attacks';

const shields: Shield[] = [9,8,7,6,5,4,3,2,1].map(sid => (
  {
    type: 'shield', slot: 'secondary',
    id: `Shield_0${sid}_FA`,
    emoji: '🛡️',
    tier: 2,
    stack: 1,
    maxLvl: 3,
    weight: 2,
    moveSpeed: -0.05,
    block: [20, 6],
    parryForce: 30,
    parryBonus: 1.5,
    skill: SkillType.Blocking,
    durability: [200, 50],
  }
));

const weapons: Weapon[] = [
  ...[1,2,3,4,5,6,7].map<Weapon>(id => ({
    type: 'weapon', slot: 'primary',
    id: `Axe_1H_0${id}_FA`,
    emoji: '🪓',
    tier: 2,
    stack: 1,
    maxLvl: 4,
    weight: 2,
    moveSpeed: -0.05,
    block: 12,
    parryForce: [20, 5],
    parryBonus: 2,
    skill: SkillType.Axes,
    toolTier: 1,
    damage: [
      dmg({ slash: 16, chop: 16 }),
      dmg({ slash: 16, chop: 16 }),
    ],
    knockback: 60,
    backstab: 3,
    attacks: [attack.swing_axe(14)],
    durability: [100, 50],
  })),
  ...[1,2,3,4,5].map<Weapon>(id => ({
    type: 'weapon', slot: 'both',
    id: `Axe2H_0${id}_FA`,
    emoji: '🪓',
    tier: 3,
    stack: 1,
    maxLvl: 4,
    weight: 2.5,
    moveSpeed: -0.2,
    block: 28,
    parryForce: [70, 5],
    parryBonus: 2,
    skill: SkillType.Axes,
    toolTier: 3,
    damage: [
      dmg({ slash: 16, chop: 16 }),
      dmg({ slash: 16, chop: 16 }),
    ],
    knockback: 70,
    backstab: 3,
    attacks: [attack.battleaxe_attack(16), attack.battleaxe_secondary(8)],
    durability: [200, 50],
  })),
  ...[1,2,3].map<Weapon>(id => ({
    type: 'weapon', slot: 'both',
    id: `Hammer_2H_0${id}_FA`,
    emoji: '🔨',
    tier: 2,
    floating: true,
    stack: 1,
    maxLvl: 4,
    weight: 2,
    moveSpeed: -0.05,
    block: 12,
    parryForce: [30, 5],
    parryBonus: 2,
    skill: SkillType.Clubs,
    damage: [dmg({ blunt: 16 }), dmg({ blunt: 16 })],
    knockback: 80,
    backstab: 3,
    attacks: [attack.swing_longsword(8), attack.mace_secondary(16)],
    durability: [200, 50],
  })),
  ...[1,2,3,4,5,6].map<Weapon>(id => ({
    type: 'weapon', slot: 'both',
    id: `Staff_2H_0${id}_FA`,
    emoji: '🥍',
    tier: 2,
    stack: 1,
    maxLvl: 4,
    weight: 2.5,
    moveSpeed: -0.05,
    block: 16,
    parryForce: [40, 5],
    parryBonus: 2,
    skill: SkillType.Polearms,
    damage: [dmg({ blunt: 16 }), dmg({ blunt: 16 })],
    knockback: 30,
    backstab: 3,
    attacks: [attack.atgeir_attack(12), attack.atgeir_secondary(24)],
    durability: [125, 50],
  })),
  ...[1,2,3,4,5].map<Weapon>(id => ({
    type: 'weapon', slot: 'primary',
    id: `Sword_1H_0${id}_FA`,
    emoji: '🗡️',
    tier: 2,
    stack: 1,
    maxLvl: 4,
    weight: 0.8,
    moveSpeed: -0.05,
    block: 12,
    parryForce: [20, 5],
    parryBonus: 2,
    skill: SkillType.Swords,
    damage: [dmg({ slash: 16 }), dmg({ slash: 16 })],
    knockback: 40,
    backstab: 3,
    attacks: [attack.swing_longsword(8), attack.sword_secondary(16)],
    durability: [200, 50],
  })),
  ...[1,2,3,4,5,6].map<Weapon>(id => ({
    type: 'weapon', slot: 'both',
    id: `Sword_2H_0${id}_FA`,
    emoji: '🗡️',
    tier: 2,
    stack: 1,
    maxLvl: 4,
    weight: 0.8,
    moveSpeed: -0.05,
    block: 12,
    parryForce: [20, 5],
    parryBonus: 2,
    skill: SkillType.Swords,
    damage: [dmg({ slash: 18 }), dmg({ slash: 18 })],
    knockback: 40,
    backstab: 3,
    attacks: [attack.greatsword(8), attack.greatsword_secondary(16)],
    durability: [200, 50],
  })),
];

export const data: GameObject[] = [
  ...shields,
  ...weapons,
].map(item => ({ ...item, mod: 'Fantasy_Armoury' }) as GameObject)

export const recipes: ItemRecipe[] = [
  ...shields.map(({ id }) => forgeRecipe(1, { Bronze: 10, Wood: 2 }, { Bronze: 3, Wood: 3, DeerHide: 3, Iron: 3 }, id)),
  ...weapons.map(({ id }) => forgeRecipe(1, { Bronze: 10, Wood: 2 }, { Bronze: 3, Wood: 3, DeerHide: 3, Iron: 3 }, id)),
];
